import { useContext, useEffect, useState } from "react";
import { LocaleContext, LoginContext, OverlayContext, StateContext } from "../common/App";
import "./Match.css";
import { ClosePopup, HelpBtn, RadioCheckButton, SquareButton, TabSwitch, TextInput, TextInputMultiline, UploadInput } from "../common/Common";
import { MatchStatus, MediationStatus, UploadInputState, UserType } from "../common/enums.mjs";
import { postWithCsrf, getAgeStr, showMatch, compareMediationStatus, compareMediationStatus2 } from "../common/util";
import { ConsumerContext } from "..";
import { LoadButton } from "../common/Common";
import { LoadState } from "../common/enums.mjs";
import { Loading } from "../common/Common";
import { ErrorComp } from "../common/Common";
import { useAutoLoad } from "../common/hooks";
import { maxDeclineReasonCustomerLength } from "../common/globals_common.mjs";

export const Match = ({userData}) => {
    const locale = useContext(LocaleContext);
    const login = useContext(LoginContext);
    const consumer = useContext(ConsumerContext);

    const [match, setMatch] = useState(null);

    // EFFECTS:

    const [loadState, setLoadState] = useAutoLoad((json) => {
        // On load success
        setMatch(json);

        // setLoadState(LoadState.LOADING); // For testing
    }, () => {
        // On load error
    }, () => {
        // On before load

        if(showMatch(consumer)) {
            return "/user/get_match";
        }

        return null;
    });


    // RENDER:

    let descr = locale.msgs.MY_MATCH_DUMMY_DESCR;
    let content = null;
    let matchBoxClasses = "match";

    if(loadState === LoadState.LOADING) {
        content = (
            <div className="side_padding">
                <Loading fixed={true}/>
            </div>
        );
    }
    else if(match === null) {
        descr = (
            <div className="text_2 margin_top_low_low">
                <ErrorComp text={locale.msgs.GENERAL_ERROR_DESCR}/>
            </div>
        );
    }
    else if(consumer.mediationStatus === MediationStatus.MATCH_FOUND) {
        if(match !== null) {
            descr = (
                <div className="text_2 margin_top_low_low">
                    {login.userType === UserType.NANNY ? locale.msgs.MY_MATCH_FOUND_DESCR_NANNY : locale.msgs.MY_MATCH_FOUND_DESCR_FAMILY}
                </div>
            );

            content = <FirstConfirmation userData={userData} match={match}/>;
        }
    }
    else if (compareMediationStatus(consumer.mediationStatus, MediationStatus.FINAL_CONFIRMATION)){
        if(match !== null) {
            descr = (
                <div className="text_2 margin_top_low_low">
                    {login.userType === UserType.NANNY ? locale.msgs.MY_MATCH_FINAL_DESCR_NANNY : locale.msgs.MY_MATCH_FINAL_DESCR_FAMILY}
                </div>
            );

            content = <FinalConfirmation userData={userData} match={match}/>;
            matchBoxClasses += " match_final tab_switch_ext_bottom_paading";
        }
    }
    
    return (
        <div className={matchBoxClasses}>
            <div className="profile_box_top">
                <div className="head profile_head">{locale.msgs.MY_MATCH}</div>
                {descr}
            </div>

            { content }
        </div>
    );
}

const FirstConfirmation = ({userData, match}) => {
    const [helpDisplayed, setHelpDisplayed] = useState(false);
    const [confirmPopupDisplayed, setConfirmPopupDisplayed] = useState(false);
    const [declinePopupDisplayed, setDeclinePopupDisplayed] = useState(false);
    const [reasonForDecline, setReasonForDecline] = useState("");
    const [reasonForDeclineError, setReasonForDeclineError] = useState(false);
    const [matchConfirmed, setMatchConfirmed] = useState(false);
    const [matchDeclined, setMatchDeclined] = useState(false);
    const [confirmedUndoable, setConfirmedUndoable] = useState(false);
    const [confirmLoadState, setConfirmLoadState] = useState(LoadState.NONE);
    const [declineLoadState, setDeclineLoadState] = useState(LoadState.NONE);

    const locale = useContext(LocaleContext);
    const login = useContext(LoginContext);
    const state = useContext(StateContext);
    const overlay = useContext(OverlayContext);

    function handleConfirmMatch() {
        try {
            postWithCsrf("/user/confirm_match", "", state).then(response => {
                if(response.ok) {
                    setMatchConfirmed(true);
                    setMatchDeclined(false);
                    setConfirmedUndoable(true);

                    setConfirmPopupDisplayed(false);
                    setConfirmLoadState(LoadState.LOAD_SUCCESS);

                    // As the mediationStatus can change, it is possible that ClosePopup will not be rerendered anymore and the overlay would not be resetted
                    overlay.setOverlay(false);
                }
                else {
                    throw new Error("Response code: " + response.status);
                }
            }).catch(error => {
                console.log(error);
                setConfirmLoadState(LoadState.LOAD_ERROR);
            });

            // Calling setConfirmLoadState() after postWithCsrf() as it could throw an exception
            setConfirmLoadState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }

    function handleDeclineMatch() {
        try {
            postWithCsrf("/user/decline_match", JSON.stringify({"reason_for_decline": reasonForDecline}), state).then(response => {
                if(response.ok) {
                    setMatchConfirmed(false);
                    setMatchDeclined(true);

                    setDeclinePopupDisplayed(false);
                    setDeclineLoadState(LoadState.LOAD_SUCCESS);

                    // As the mediationStatus can change, it is possible that ClosePopup will not be rerendered anymore and the overlay would not be resetted
                    overlay.setOverlay(false);
                }
                else {
                    throw new Error("Response code: " + response.status);
                }
            }).catch(error => {
                console.log(error);
                setDeclineLoadState(LoadState.LOAD_ERROR);
            });

            // Calling setDeclineLoadState() after postWithCsrf() as it could throw an exception
            setDeclineLoadState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }

    useEffect(() => {
        setMatchConfirmed(match["confirmed"]);
        setMatchDeclined(match["declined"]);
        setConfirmedUndoable(match["confirmed_undoable"]);
    }, [match]);

    return (
        <div>
            <div className="match_suggestion_box">
                <div className="text_big_3">{login.userType === UserType.NANNY ? locale.msgs.OUR_SUGGESTION_FOR_FAMILY : locale.msgs.OUR_SUGGESTION_FOR_NANNY}</div>

                <MatchInfo userData={userData} match={match} />

                <div className="match_confirm_box">
                    <div className="text_big_3 match_confirm_text">
                        {locale.msgs.DO_YOU_WANT_CONFIRM_MATCH}
                        <HelpBtn onClick={(e) => setHelpDisplayed(true) } />
                    </div>

                    <div className="match_confirm_help_box">
                        <ClosePopup helpDisplayed={helpDisplayed}
                                onClose={() => setHelpDisplayed(false) }
                                content={
                                    <div className="share_help_content">
                                        
                                        <div className="text_big_3 margin_top_low_low">{locale.msgs.DO_YOU_WANT_CONFIRM_MATCH}</div>
                                        <div dangerouslySetInnerHTML={{__html: login.userType === UserType.NANNY ? locale.msgs.DO_YOU_WANT_CONFIRM_MATCH_DESCR_NANNY : locale.msgs.DO_YOU_WANT_CONFIRM_MATCH_DESCR_FAMILY}}
                                                className="text_light_2 margin_top_low_low"></div>
                                    </div>
                                }
                        />
                    </div>
                </div>

                {
                    matchConfirmed ?
                        <div className="match_confirmed_suc_box_box">
                            <div className="match_confirmed_suc_box">
                                <img className="match_confirmed_suc_img" src="/common/icon_done_green.svg"></img>
                                <div className="match_confirmed_suc_text text_success_2">{locale.msgs.MATCH_CONFIRMED}</div>
                            </div>

                            { confirmedUndoable ? 
                                <div className="text_small_small_2 cancel_confirmation noselect"
                                        onClick={() => setDeclinePopupDisplayed(true)}>
                                    {locale.msgs.CANCEL_CONFIRMATION}
                                </div>
                            :
                                null
                            }
                            
                        </div>
                    :
                        matchDeclined ?
                            <div className="match_confirmed_suc_box_box">
                                <div className="match_confirmed_suc_box">
                                    <img className="match_confirmed_suc_img" src="/common/icon_close_red.svg"></img>
                                    <div className="match_confirmed_suc_text text_error_2">{locale.msgs.MATCH_DECLINED}</div>
                                </div>

                                <div className="text_small_small_2 cancel_confirmation noselect"
                                        onClick={() => setConfirmPopupDisplayed(true)}>
                                    {locale.msgs.CANCEL_DECLINE}
                                </div>
                            </div>
                        :
                            <div className="match_confirm_btn_box">
                                <SquareButton text={locale.msgs.DECLINE} white={true} clickable={true} onClick={() => { setDeclinePopupDisplayed(true)}} />
                                <SquareButton text={locale.msgs.CONFIRM} clickable={true} onClick={() => { setConfirmPopupDisplayed(true)}} />
                            </div>
                }
                
            </div>

            <ClosePopup fixed={true} helpDisplayed={confirmPopupDisplayed}
                    onClose={() => setConfirmPopupDisplayed(false) }
                    content={
                        <div className="popup_box_content">
                            <div className="head profile_head">{locale.msgs.ARE_YOU_SURE}</div>
                            <div dangerouslySetInnerHTML={{__html: login.userType === UserType.NANNY ? locale.msgs.CONFIRM_MATCH_DESCR_NANNY : locale.msgs.CONFIRM_MATCH_DESCR_FAMILY}}
                                    className="text_2 margin_top_low_low"></div>

                            <div className="btn_right">
                                <LoadButton text={locale.msgs.CONFIRM} clickable={true} onClick={handleConfirmMatch}
                                        saving={confirmLoadState === LoadState.LOADING}
                                        notSaved={confirmLoadState === LoadState.LOAD_ERROR}
                                        errText={locale.msgs.ERROR} />
                            </div>
                        </div>
                    }
            />

            <ClosePopup fixed={true} helpDisplayed={declinePopupDisplayed}
                    onClose={() => setDeclinePopupDisplayed(false) }
                    content={
                        <div className="popup_box_content">
                            <div className="head profile_head">{locale.msgs.ARE_YOU_SURE}</div>
                            <div className="text_2 margin_top_low_low"
                                    dangerouslySetInnerHTML={
                                        {
                                            __html: matchConfirmed ?
                                                    (login.userType === UserType.NANNY ? locale.msgs.CANCEL_CONFIRMATION_DESCR_NANNY : locale.msgs.CANCEL_CONFIRMATION_DESCR_FAMILY)
                                                :
                                                    (login.userType === UserType.NANNY ? locale.msgs.DECLINE_MATCH_DESCR_NANNY : locale.msgs.DECLINE_MATCH_DESCR_FAMILY)
                                        }
                                    }
                            ></div>

                            <div className="text_light_2 reason_for_decline_text">{locale.msgs.REASON_FOR_DECLINE}</div>

                            <TextInputMultiline text={locale.msgs.REASON_FOR_DECLINE} value={reasonForDecline}
                                    onChange={(value) => setReasonForDecline(value)}
                                    onError={setReasonForDeclineError}
                                    showPlaceholderIfNotEmpty={false} height="15rem"
                                    maxLength={maxDeclineReasonCustomerLength} />

                            <div className="btn_right">
                                <LoadButton text={locale.msgs.DECLINE} clickable={reasonForDecline.length <= maxDeclineReasonCustomerLength && !reasonForDeclineError}
                                        onClick={handleDeclineMatch}
                                        saving={declineLoadState === LoadState.LOADING}
                                        notSaved={declineLoadState === LoadState.LOAD_ERROR}
                                        errText={locale.msgs.ERROR} />
                            </div>
                        </div>
                    }
            />
        </div>
    )
}

const FinalConfirmation = ({userData, match}) => {
    const locale = useContext(LocaleContext);
    const login = useContext(LoginContext);
    const state = useContext(StateContext);
    const overlay = useContext(OverlayContext);
    const consumer = useContext(ConsumerContext);

    const [helpDisplayed, setHelpDisplayed] = useState(false);
    const [confirmPopupDisplayed, setConfirmPopupDisplayed] = useState(false);
    const [declinePopupDisplayed, setDeclinePopupDisplayed] = useState(false);
    const [alreadyMetPopupDisplayed, setAlreadyMetPopupDisplayed] = useState(false);
    const [doNotShowAlreadyMetPopup, setDoNotShowAlreadyMetPopup] = useState(false /*localStorage.getItem(login.username + "_do_not_show_final_confirmation_popup") === "true"*/);
    const [tabIdx, setTabIdx] = useState(0);
    const [matchConfirmed, setMatchConfirmed] = useState(false);
    const [matchDeclined, setMatchDeclined] = useState(false);
    const [reasonForDecline, setReasonForDecline] = useState("");
    const [reasonForDeclineError, setReasonForDeclineError] = useState(false);
    const [confirmLoadState, setConfirmLoadState] = useState(LoadState.NONE);
    const [declineLoadState, setDeclineLoadState] = useState(LoadState.NONE);

    function handleConfirmMatch() {
        try {
            postWithCsrf("/user/confirm_match_final", "", state).then(response => {
                if(response.ok) {
                    setConfirmLoadState(LoadState.LOAD_SUCCESS);
                    
                    setMatchConfirmed(true);
                    setMatchDeclined(false);

                    setConfirmPopupDisplayed(false);

                    // As the mediationStatus can change, it is possible that ClosePopup will not be rerendered anymore and the overlay would not be resetted
                    overlay.setOverlay(false);
                }
                else {
                    throw new Error("Response code: " + response.status);
                }
            }).catch(error => {
                setConfirmLoadState(LoadState.LOAD_ERROR);
                console.log(error);
            });

            // Calling setConfirmLoadState() after postWithCsrf() as it could throw an exception
            setConfirmLoadState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }

    function handleDeclineMatch() {
        try {
            postWithCsrf("/user/decline_match_final", JSON.stringify({"reason_for_decline": reasonForDecline}), state).then(response => {
                if(response.ok) {
                    setDeclineLoadState(LoadState.LOAD_SUCCESS);

                    setMatchConfirmed(false);
                    setMatchDeclined(true);

                    setDeclinePopupDisplayed(false);

                    // As the mediationStatus can change, it is possible that ClosePopup will not be rerendered anymore and the overlay would not be resetted
                    overlay.setOverlay(false);
                }
                else {
                    throw new Error("Response code: " + response.status);
                }
            }).catch(error => {
                setDeclineLoadState(LoadState.LOAD_ERROR);
                console.log(error);
            });

            // Calling setDeclineLoadState() after postWithCsrf() as it could throw an exception
            setDeclineLoadState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }

    useEffect(() => {
        setMatchConfirmed(match["confirmed_final"]);
        setMatchDeclined(match["declined_final"]);
    }, [match]);

    return (
        <div>
            <TabSwitch tabNames={[locale.msgs.CONTACT_DATA, locale.msgs.FINAL_CONFIRMATION]}
                    tabElements={[
                        <div className="match_contact_data_box">
                            <div className="text_big_3 match_contact_data_head">{login.userType === UserType.NANNY ? locale.msgs.YOUR_FAMILY : locale.msgs.YOUR_NANNY}</div>
                            <MatchInfo userData={userData} match={match} finalConfirmation={true} />
                        </div>,

                        <div className="match_suggestion_box">
                            <div className="match_confirm_box">
                                <div className="text_big_3 match_confirm_text match_confirm_text_final">
                                    <div dangerouslySetInnerHTML={{__html: locale.msgs.DO_YOU_WANT_CONFIRM_MATCH_FINAL}}></div>
                                    <HelpBtn onClick={(e) => setHelpDisplayed(true) } />
                                </div>
            
                                <div className="match_confirm_help_box">
                                    <ClosePopup helpDisplayed={helpDisplayed}
                                            onClose={() => setHelpDisplayed(false) }
                                            content={
                                                <div className="share_help_content">
                                                    
                                                    <div className="text_big_3 margin_top_low_low"
                                                            dangerouslySetInnerHTML={{__html: locale.msgs.DO_YOU_WANT_CONFIRM_MATCH_FINAL}}>    
                                                    </div>
            
                                                    <div dangerouslySetInnerHTML={{__html: login.userType === UserType.NANNY ? locale.msgs.DO_YOU_WANT_CONFIRM_MATCH_FINAL_DESCR_2_NANNY : locale.msgs.DO_YOU_WANT_CONFIRM_MATCH_FINAL_DESCR_2_FAMILY}}
                                                            className="text_light_2 margin_top_low_low"></div>
                                                </div>
                                            }
                                    />
                                </div>
                            </div>

                            <div className="text_light_2 confirm_match_final_descr">
                                { login.userType === UserType.NANNY ?
                                    locale.msgs.DO_YOU_WANT_CONFIRM_MATCH_FINAL_DESCR_NANNY
                                :
                                    locale.msgs.DO_YOU_WANT_CONFIRM_MATCH_FINAL_DESCR_FAMILY
                                }
                            </div>
        
                            { matchConfirmed ?
                                <div className="match_confirmed_suc_box_box">
                                    <div className="match_confirmed_suc_box match_confirmed_suc_box_final">
                                        <img className="match_confirmed_suc_img" src="/common/icon_done_green.svg"></img>
                                        <div className="match_confirmed_suc_text text_success_2">{locale.msgs.MATCH_CONFIRMED_FINAL}</div>
                                    </div>
        
                                    { compareMediationStatus2(consumer.mediationStatus, MediationStatus.FINAL_CONFIRMATION) ?
                                        null
                                    :
                                        <div className="text_small_small_2 cancel_confirmation noselect"
                                                onClick={() => setDeclinePopupDisplayed(true)}>
                                            {locale.msgs.CANCEL_CONFIRMATION}
                                        </div>
                                    }
                                </div>
                            :
                                matchDeclined ?
                                    <div className="match_confirmed_suc_box_box">
                                        <div className="match_confirmed_suc_box match_confirmed_suc_box_final">
                                            <img className="match_confirmed_suc_img" src="/common/icon_close_red.svg"></img>
                                            <div className="match_confirmed_suc_text text_error_2">{locale.msgs.MATCH_DECLINED_FINAL}</div>
                                        </div>

                                        { match.change_of_final_possible ? 
                                            <div className="text_small_small_2 cancel_confirmation noselect"
                                                    onClick={() => setConfirmPopupDisplayed(true)}>
                                                {locale.msgs.CANCEL_DECLINE}
                                            </div>
                                        :
                                            null
                                        }

                                    </div>
                                :
                                    <div className="match_confirm_btn_box match_confirm_btn_box_final">
                                        <SquareButton text={locale.msgs.DECLINE} white={true} clickable={true} onClick={() => { setDeclinePopupDisplayed(true)}} />
                                        <SquareButton text={locale.msgs.CONFIRM} clickable={true} onClick={() => { setConfirmPopupDisplayed(true)}} />
                                    </div>
                            }
                        </div>
                    ]}
                    
                    // If this click handler returns false, the tab switch will not be performed
                    clickHandler={(idx) => {
                        if(idx == 1 && !doNotShowAlreadyMetPopup && !matchConfirmed && !matchDeclined) {
                            setAlreadyMetPopupDisplayed(true);
                            return false;
                        }

                        return true;
                    }}
                    currentTabIdx={tabIdx}
                    setTabIdx={setTabIdx}
            />

            <ClosePopup fixed={true} helpDisplayed={confirmPopupDisplayed}
                    onClose={() => setConfirmPopupDisplayed(false) }
                    content={
                        <div className="popup_box_content">
                            <div className="head profile_head">{locale.msgs.ARE_YOU_SURE}</div>
                            <div dangerouslySetInnerHTML={{__html: login.userType === UserType.NANNY ? locale.msgs.CONFIRM_MATCH_FINAL_DESCR_NANNY : locale.msgs.CONFIRM_MATCH_FINAL_DESCR_FAMILY}}
                                    className="text_2 margin_top_low_low"></div>

                            <div className="btn_right">
                                <LoadButton text={locale.msgs.CONFIRM} clickable={true} onClick={handleConfirmMatch}
                                        saving={confirmLoadState === LoadState.LOADING}
                                        notSaved={confirmLoadState === LoadState.LOAD_ERROR}
                                        errText={locale.msgs.ERROR} />
                            </div>
                        </div>
                    }
            />

            <ClosePopup fixed={true} helpDisplayed={declinePopupDisplayed}
                    onClose={() => setDeclinePopupDisplayed(false) }
                    content={
                        <div className="popup_box_content">
                            <div className="head profile_head">{locale.msgs.ARE_YOU_SURE}</div>
                            <div className="text_2 margin_top_low_low"
                                    dangerouslySetInnerHTML={
                                        {
                                            __html: matchConfirmed ?
                                                    (login.userType === UserType.NANNY ? locale.msgs.CANCEL_CONFIRMATION_FINAL_DESCR_NANNY : locale.msgs.CANCEL_CONFIRMATION_FINAL_DESCR_FAMILY)
                                                :
                                                    (login.userType === UserType.NANNY ? locale.msgs.DECLINE_MATCH_FINAL_DESCR_NANNY : locale.msgs.DECLINE_MATCH_FINAL_DESCR_FAMILY)
                                        }
                                    }
                            ></div>

                            <div className="text_light_2 reason_for_decline_text">{locale.msgs.REASON_FOR_DECLINE}</div>

                            <TextInputMultiline text={locale.msgs.REASON_FOR_DECLINE} value={reasonForDecline}
                                    onChange={(value) => setReasonForDecline(value)}
                                    onError={setReasonForDeclineError}
                                    showPlaceholderIfNotEmpty={false} height="15rem"
                                    maxLength={maxDeclineReasonCustomerLength} />

                            <div className="btn_right">
                                <LoadButton text={locale.msgs.DECLINE} clickable={reasonForDecline.length <= maxDeclineReasonCustomerLength && !reasonForDeclineError}
                                        onClick={handleDeclineMatch}
                                        saving={declineLoadState === LoadState.LOADING}
                                        notSaved={declineLoadState === LoadState.LOAD_ERROR}
                                        errText={locale.msgs.ERROR} />
                            </div>
                        </div>
                    }
            />

            <ClosePopup fixed={true} helpDisplayed={alreadyMetPopupDisplayed}
                    onClose={() => setAlreadyMetPopupDisplayed(false) }
                    content={
                        <div className="popup_box_content">
                            <div className="head profile_head">{locale.msgs.ALREADY_MET + "?"}</div>
                            <div dangerouslySetInnerHTML={{__html: login.userType === UserType.NANNY ? locale.msgs.ALREADY_MET_DESCR_NANNY : locale.msgs.ALREADY_MET_DESCR_FAMILY}}
                                    className="text_2 margin_top_low_low">

                            </div>

                            {/*
                            <div className="do_not_show_checkbox">
                                <RadioCheckButton tiny={true} type="checkbox" text={locale.msgs.DO_NOT_SHOW_AGAIN} id="5" name="musically"
                                        checked={ doNotShowAlreadyMetPopup }
                                        onChange={ (e) => {
                                                setDoNotShowAlreadyMetPopup(e.target.checked);
                                                localStorage.setItem(login.username + "_do_not_show_final_confirmation_popup", e.target.checked);
                                            }}
                                />
                            </div>
                            */}

                            <div className="btn_right">
                                <SquareButton text={locale.msgs.ALREADY_MET} clickable={true}
                                        onClick={() => {
                                            setTabIdx(1);
                                            setAlreadyMetPopupDisplayed(false);
                                        }}
                                />
                            </div>
                        </div>
                    }
            />
        </div>
    )
}

const MatchInfo = ({match, finalConfirmation}) => {
    const locale = useContext(LocaleContext);
    const login = useContext(LoginContext);

    if(login.userType === UserType.NANNY) {
        var children = [];

        // Number of children
        children.push(
            <TextInput minimal={true} readOnly={true} text={locale.msgs.NO_OF_CHILDREN}
                    value={match["childrens_age"].length}
                    onChange={(value) => {}} />
        );

        for(let i = 0; i < match["childrens_age"].length; i++) {
            const age = match["childrens_age"][i];

            children.push(
                <TextInput minimal={true} readOnly={true} text={ locale.msgs.AGE_OF_CHILD !== undefined ? (locale.msgs.AGE_OF_CHILD.replace("${{num}}", (i + 1))) : ""}
                        value={getAgeStr(age, locale)}
                        onChange={(value) => {}} />
            );
        }
    }
    else if(login.userType === UserType.FAMILY) {
        var documents = [];

        for(let [k, v] of Object.entries(match["documents"])) {
            documents.push(
                <UploadInput name="" filename={v.name}
                        editable={false} state={UploadInputState.UPLOADED}
                        url={v.url}
                        removeBtnPresent={false}
                        removable={false}
                />
            );
        }
    }

    return (
        <div className="match_suggestion_data_box">
            <div className="match_suggestion_hori_box">
                { login.userType === UserType.NANNY ?
                    <TextInput minimal={true} readOnly={true} text={locale.msgs.CUSTOMER_NO} value={match["family_id"]}
                        onChange={(value) => {}} />
                : [
                    <TextInput minimal={true} readOnly={true} text={locale.msgs.FIRST_NAME} value={match["first_name"]}
                        onChange={(value) => {}} />
                ]}
            </div>

            {
                login.userType === UserType.NANNY ?
                    children
                :
                    documents
            }

            { finalConfirmation ?
                <TextInput minimal={true} readOnly={true} text={locale.msgs.EMAIL} value={match["email"]}
                        onChange={(value) => {}} />
            :
                null
            }

            { finalConfirmation ?
                <TextInput minimal={true} readOnly={true} text={locale.msgs.TELEPHONE_NO} value={match["phone_number"]}
                onChange={(value) => {}} />
            :
                null
            }
        </div>
    );
}
